import { CheckCircle2, GitBranch, Loader2, ThumbsDown, ThumbsUp, XCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { useState } from 'react'
import { useAppDispatch } from '@/redux/hooks'
import { voteRenegotiationThunk } from '../../redux/asyncThunks/charterThunks'
import type { RenegotiationRequest } from '../../redux/types/charterTypes'

interface RenegotiationVotePanelProps {
  request: RenegotiationRequest
  roomId: string
  teamId: number
  currentMemberId: string
  memberNames: Record<string, string>
}

export function RenegotiationVotePanel({
  request,
  roomId,
  teamId,
  currentMemberId,
  memberNames,
}: RenegotiationVotePanelProps) {
  const dispatch = useAppDispatch()
  const [isVoting, setIsVoting] = useState(false)

  const myVote = request.votes[currentMemberId]
  const hasVoted = myVote !== undefined
  const yesCount = Object.values(request.votes).filter((v) => v.vote).length
  const totalVotes = Object.keys(request.votes).length

  const handleVote = async (vote: boolean) => {
    if (hasVoted) return
    setIsVoting(true)
    try {
      await dispatch(voteRenegotiationThunk({ roomId, teamId, vote }))
    } finally {
      setIsVoting(false)
    }
  }

  const statusConfig = {
    pending_votes: { label: 'Pending Votes', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-200' },
    in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    completed: { label: 'Completed', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  }[request.status] ?? { label: request.status, className: 'bg-gray-100 text-gray-600' }

  return (
    <Card className='border-amber-200 bg-amber-50'>
      <CardHeader className='pb-2'>
        <div className='flex items-center justify-between'>
          <CardTitle className='text-sm font-semibold text-amber-900 flex items-center gap-2'>
            <GitBranch className='h-4 w-4' />
            Amendment Request
          </CardTitle>
          <Badge variant='outline' className={statusConfig.className}>
            {statusConfig.label}
          </Badge>
        </div>
        <p className='text-xs text-amber-700'>
          Proposed by <strong>{request.requested_by_name}</strong>
        </p>
      </CardHeader>
      <CardContent className='space-y-4'>
        {/* Sections to amend */}
        <div>
          <p className='text-xs font-semibold text-amber-800 mb-1'>Sections to re-negotiate:</p>
          <div className='flex flex-wrap gap-1'>
            {request.sections_to_amend.map((s) => (
              <Badge
                key={s}
                variant='outline'
                className='text-xs border-amber-300 text-amber-700 bg-white'
              >
                {s.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        </div>

        {/* Reason */}
        <div>
          <p className='text-xs font-semibold text-amber-800 mb-1'>Reason:</p>
          <p className='text-sm text-amber-900'>{request.reason}</p>
        </div>

        <Separator className='bg-amber-200' />

        {/* Vote tally */}
        <div className='space-y-1.5'>
          <p className='text-xs font-semibold text-amber-800'>
            Votes: {yesCount} yes / {Object.values(request.votes).filter((v) => !v.vote).length} no ({totalVotes} cast)
          </p>
          {Object.entries(memberNames).map(([id, name]) => {
            const vote = request.votes[id]
            return (
              <div key={id} className='flex items-center justify-between text-xs'>
                <span className='text-amber-800'>
                  {name}
                  {id === currentMemberId && ' (you)'}
                </span>
                {vote !== undefined ? (
                  vote.vote ? (
                    <CheckCircle2 className='h-4 w-4 text-emerald-500' />
                  ) : (
                    <XCircle className='h-4 w-4 text-red-500' />
                  )
                ) : (
                  <span className='text-gray-400 italic'>Pending…</span>
                )}
              </div>
            )
          })}
        </div>

        {/* Vote buttons */}
        {request.status === 'pending_votes' && !hasVoted && (
          <div className='flex gap-2'>
            <Button
              onClick={() => handleVote(true)}
              disabled={isVoting}
              size='sm'
              className='flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5'
            >
              {isVoting ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <ThumbsUp className='h-3.5 w-3.5' />}
              Approve
            </Button>
            <Button
              onClick={() => handleVote(false)}
              disabled={isVoting}
              size='sm'
              variant='outline'
              className='flex-1 border-red-300 text-red-600 hover:bg-red-50 gap-1.5'
            >
              <ThumbsDown className='h-3.5 w-3.5' />
              Reject
            </Button>
          </div>
        )}

        {hasVoted && (
          <p className='text-xs text-amber-800'>
            {myVote.vote ? '✓ You approved this amendment.' : '✗ You rejected this amendment.'}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
